import { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import useRequest from "../../api/useRequest";
import { tokens } from "../../theme/theme";
import CreateTenantDialog from "./CreateTenantDialog";
import InviteUserDialog from "./InviteUserDialog";

const memberName = (member, fallback) =>
  [member.firstName, member.lastName].filter(Boolean).join(" ").trim() ||
  member.email ||
  fallback;

const memberInitials = (member) =>
  [member.firstName, member.lastName]
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const Team = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { apiData, isLoading, serverError, reloadApiData } = useRequest({
    path: "tenant/members",
  });
  const members = useMemo(() => apiData?.members || [], [apiData]);
  const requestStatus = serverError?.error?.response?.status || serverError?.status;
  const hasNoTenant = requestStatus === 404 && !apiData;

  const visibleMembers = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    if (!normalizedSearch) return members;
    return members.filter((member) =>
      [member.firstName, member.lastName, member.email]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase()
        .includes(normalizedSearch)
    );
  }, [members, search]);

  const handleDataChanged = () => {
    setInviteDialogOpen(false);
    reloadApiData();
  };

  return (
    <Box className="flex flex-col gap-6">
      <Box className="flex flex-row justify-between items-start gap-4 flex-wrap">
        <Box>
          <Typography variant="h2">{t("Team")}</Typography>
          <Typography variant="h4" sx={{ color: colors.contrast[700], mt: 1 }}>
            {t("Members and pending invitations in your organization.")}
          </Typography>
        </Box>
        {!hasNoTenant && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<GroupAddIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            {t("Invite user")}
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box
          className="flex flex-col justify-center items-center gap-3 p-8"
          sx={{ minHeight: 260 }}
          aria-live="polite"
        >
          <CircularProgress aria-label={t("Loading team...")} />
          <Typography variant="h5">{t("Loading team...")}</Typography>
        </Box>
      ) : hasNoTenant ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" onClick={() => setTenantDialogOpen(true)}>
              {t("Create organization")}
            </Button>
          }
        >
          {t("Your user account is not assigned to an organization.")}
        </Alert>
      ) : serverError && !apiData ? (
        <Alert
          severity="error"
          action={<Button onClick={reloadApiData}>{t("Try again")}</Button>}
        >
          {t("Team could not be loaded.")}
        </Alert>
      ) : (
        <Box className="flex flex-col gap-3">
          <TextField
            label={t("Search team")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="small"
            sx={{ maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TableContainer component={Paper}>
            <Table aria-label={t("Team members")}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("Profile")}</TableCell>
                  <TableCell>{t("Name")}</TableCell>
                  <TableCell>{t("Email")}</TableCell>
                  <TableCell>{t("Status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {members.length === 0
                        ? t("No team members yet.")
                        : t("No team members match your search.")}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleMembers.map((member) => (
                    <TableRow key={member.user_id} hover>
                      <TableCell>
                        <Avatar
                          src={member.profile_picture_url || undefined}
                          alt={memberName(member, t("Unknown user"))}
                        >
                          {memberInitials(member)}
                        </Avatar>
                      </TableCell>
                      <TableCell>{memberName(member, t("Unknown user"))}</TableCell>
                      <TableCell>{member.email || "–"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={member.status === "active" ? "success" : "warning"}
                          label={t(member.status === "active" ? "Active" : "Invited")}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onInvited={handleDataChanged}
        onTenantCreated={reloadApiData}
      />
      <CreateTenantDialog
        open={tenantDialogOpen}
        onClose={() => setTenantDialogOpen(false)}
        onTenantCreated={() => {
          setTenantDialogOpen(false);
          reloadApiData();
        }}
      />
    </Box>
  );
};

export default Team;
